<script setup>
/**
 * Who owns a case: one person, or one team, or nobody.
 *
 * A team is a first-class owner here, not a fallback for "unassigned", so the
 * chip renders a team as deliberately as it renders a person. Unassigned is
 * also a real state and says so in words rather than showing an empty slot.
 * One size everywhere — list rows and the workroom header render owners
 * identically, which is the point of the kit.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { OWNER_TYPE } from 'dashboard/helper/supportCaseMeta';

const props = defineProps({
  // `{ type, id, name, thumbnail }` as built by `presentOwner`; null = unassigned.
  owner: {
    type: Object,
    default: null,
  },
});

const AVATAR_SIZE = 20;

const { t } = useI18n();

const isTeam = computed(() => props.owner?.type === OWNER_TYPE.TEAM);

const label = computed(
  () => props.owner?.name || t('SUPPORT_CASES.OWNER.UNASSIGNED')
);

const ariaLabel = computed(() => {
  if (!props.owner) return t('SUPPORT_CASES.OWNER.UNASSIGNED');
  return isTeam.value
    ? t('SUPPORT_CASES.OWNER.TEAM_ARIA', { name: props.owner.name })
    : t('SUPPORT_CASES.OWNER.USER_ARIA', { name: props.owner.name });
});
</script>

<template>
  <span
    class="inline-flex items-center min-w-0 gap-1.5"
    :aria-label="ariaLabel"
    data-testid="owner-chip"
  >
    <span
      v-if="!owner"
      class="flex items-center justify-center rounded-md size-5 text-n-slate-10 bg-n-alpha-2"
    >
      <Icon icon="i-lucide-circle-dashed" class="size-3" />
    </span>
    <span
      v-else-if="isTeam"
      class="flex items-center justify-center rounded-md size-5 text-n-slate-11 bg-n-alpha-2"
      data-testid="owner-chip-team-icon"
    >
      <Icon icon="i-lucide-users" class="size-3" />
    </span>
    <Avatar
      v-else
      :name="owner.name"
      :src="owner.thumbnail"
      :size="AVATAR_SIZE"
      data-testid="owner-chip-avatar"
    />
    <span
      class="text-sm truncate"
      :class="owner ? 'text-n-slate-12' : 'text-n-slate-10'"
    >
      {{ label }}
    </span>
  </span>
</template>
